import {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';

interface CreateOnboardingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  prefill?: {
    groupName: string;
    rfcSubmissionId?: string;
    clientId: string;
  };
}

export function CreateOnboardingDialog({
  open,
  onOpenChange,
  clientId,
  prefill,
}: CreateOnboardingDialogProps) {
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [assignedManagerId, setAssignedManagerId] = useState('');
  const [targetDate, setTargetDate] = useState('');

  const effectiveClientId = prefill?.clientId || clientId;

  const utils = trpc.useUtils();

  const templatesQuery = trpc.internal.onboarding.listTemplates.useQuery({
    clientId: effectiveClientId,
  });
  const usersQuery = trpc.internal.userManagement.listUsers.useQuery({archived: false});

  // Apply prefill values when dialog opens
  useEffect(() => {
    if (open && prefill) {
      setGroupName(prefill.groupName);
    }
  }, [open, prefill]);

  const internalUsers = (usersQuery.data?.users ?? []).filter((u) => u.cbsInternalProfile);

  const createMutation = trpc.internal.onboarding.createOnboarding.useMutation({
    onSuccess: (data) => {
      utils.internal.onboarding.listOnboardings.invalidate();
      resetForm();
      onOpenChange(false);
      navigate(`/onboarding/${data.id}`);
    },
  });

  function resetForm() {
    setGroupName('');
    setTemplateId('');
    setAssignedManagerId('');
    setTargetDate('');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!groupName.trim() || !templateId) return;

    createMutation.mutate({
      clientId: effectiveClientId,
      groupName: groupName.trim(),
      templateId,
      assignedManagerId: assignedManagerId || undefined,
      targetCompletionDate: targetDate ? new Date(targetDate) : undefined,
      rfcSubmissionId: prefill?.rfcSubmissionId,
    });
  }

  const isValid = groupName.trim().length > 0 && templateId.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start New Onboarding</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='groupName'>Group Name</Label>
            <Input
              id='groupName'
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder='Enter group name'
              required
            />
          </div>

          <div className='space-y-2'>
            <Label>Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder='Select a template' />
              </SelectTrigger>
              <SelectContent>
                {(templatesQuery.data ?? []).map((template: {id: string; name: string}) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label>Assigned Manager</Label>
            <Select value={assignedManagerId} onValueChange={setAssignedManagerId}>
              <SelectTrigger>
                <SelectValue placeholder='Select a manager (optional)' />
              </SelectTrigger>
              <SelectContent>
                {internalUsers.map((user: {id: string; name: string; email: string}) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='targetDate'>Target Completion Date</Label>
            <Input
              id='targetDate'
              type='date'
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type='submit' disabled={!isValid || createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Onboarding'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
